import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useRoute, RouteProp } from '@react-navigation/native';
import {
  FormProvider, useForm, useWatch, FieldErrors,
} from 'react-hook-form';
import { useDialog } from '@/common/context/DialogContext';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import { confirmDiscardChanges } from '@/common/utils/confirmDiscardChanges';
import { usePreviewModal } from '@/common/components/ModulePreview/hooks/useModulePreviewStore';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useAuthStore } from '@/common/auth/store';
import log from '@/common/utils/logger';
import type { VersionHistoryEntry } from '@/common/api/models/versionHistoryEntry';
import type { ModuleLevel } from '@/common/api/models/moduleLevel';
import type { ModuleMetadataTaskCompletionPolicy } from '@/common/api/models/moduleMetadataTaskCompletionPolicy';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';
import type { ModuleUpdateRequest } from '@/common/api/models/moduleUpdateRequest';
import { ApprovalRequestApprovalType } from '@/common/api/models/approvalRequestApprovalType';
import { ModuleApprovalRequestFields, type ModuleApprovalType } from '../components/ModuleApprovalRequestFields';
import type { TaskEditorHandle } from '../TaskEditor/TaskEditor';
import { useModuleRubricJob } from './useModuleRubricJob';
import { useModuleWorkingModeApplyJob } from './useModuleWorkingModeApplyJob';
import { useModuleEditorCapabilities } from './useModuleEditorCapabilities';
import { getEligibleRubricGenerationTaskIds } from '../utils/rubricJobEligibility';

interface ModuleEditorFormData {
  description: string;
  objectives: Record<number, string[]>;
  levelTitles: Record<number, string>;
  taskCompletionPolicy: ModuleMetadataTaskCompletionPolicy;
  taskCompletionMinLevels: Record<string, number>;
  autogradeOnSubmission: boolean;
  examinationBasis: 'task_submissions' | 'oral_exam' | null;
}

/**
 * Controller hook for the ModuleEditor component.
 * Extracts business logic from the UI.
 */
export const useModuleEditorController = (
  screenNavigation: StackNavigationProp<LecturerRouteParamList, 'ModuleEditor'>,
) => {
  const { t } = useTranslation();
  const {
    showDialog, hideDialog, showToastMessage, updateDialogProps, dialogIsVisible,
  } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const { openPreview, closePreview } = usePreviewModal();
  const taskEditorRef = useRef<TaskEditorHandle | null>(null);

  const route = useRoute<RouteProp<LecturerRouteParamList, 'ModuleEditor'>>();
  const moduleId = route.params?.moduleId;
  const version = route.params?.version ?? '0.0';
  const taskId = route.params?.taskId ?? undefined;

  const [taskFormIsDirty, setTaskFormIsDirty] = useState(false);
  const [taskSavePending, setTaskSavePending] = useState(false);

  const changelogMethods = useForm({
    defaultValues: {
      approvalType: 'new_version' as ModuleApprovalType,
      changelogComment: '',
      offerStudentNote: '',
    },
  });

  const requestApprovalDialogActiveRef = useRef(false);
  const approvalTypeChoice = useWatch({
    control: changelogMethods.control,
    name: 'approvalType',
  }) as ModuleApprovalType | undefined;

  useEffect(() => {
    if (!dialogIsVisible) {
      requestApprovalDialogActiveRef.current = false;
      return;
    }
    if (!requestApprovalDialogActiveRef.current) return;
    updateDialogProps({ size: approvalTypeChoice === 'offer_upgrade' ? 'xl' : 'lg' });
  }, [approvalTypeChoice, dialogIsVisible, updateDialogProps]);

  const {
    module,
    tasks,
    isLoading,
    isModuleLoading,
    versionHistory,
    tasksQueryKey,
    moduleNotFound,
    updateModuleMutation,
    setAutogradeOnSubmissionMutation,
    requestApprovalMutation,
    createVersionMutation,
    deleteVersionMutation,
    deleteModuleMutation,
    createTaskMutation,
    updateTaskMutation,
    deleteTaskMutation,
  } = useLecturerModuleData(moduleId, version, taskId);

  const { isFeatureActive } = useUserFeatures();
  const taskAiEnabled = isFeatureActive('lecturer-task-ai');

  const {
    launch: launchRubricGenerationJob,
    isJobActive: isRubricGenerationJobActive,
    isLaunching: isLaunchingRubricJob,
  } = useModuleRubricJob({
    moduleId,
    version,
    tasksQueryKey,
  });

  // Invalidate tasks/module when apply finishes — even if the lecturer left Module Settings.
  useModuleWorkingModeApplyJob({
    moduleId,
    version,
    tasksQueryKey,
  });

  const methods = useForm<ModuleEditorFormData>();
  const {
    formState, handleSubmit, reset, getValues,
  } = methods;
  const { dirtyFields } = formState;

  // Compute readonly state
  const isAnyMutationPending = updateModuleMutation.isPending
    || requestApprovalMutation.isPending
    || createVersionMutation.isPending
    || deleteVersionMutation.isPending
    || deleteModuleMutation.isPending
    || createTaskMutation.isPending
    || updateTaskMutation.isPending
    || deleteTaskMutation.isPending;
  const isDraftVersion = module?.state?.workflowState === 'draft';
  const isCurrentVersion = !!module?.isCurrent;
  const isPublishedVersion = module?.state?.workflowState === 'published';
  const isTempTask = !!taskId?.startsWith('temp-');

  // Authorship is the permission axis for editing. Fail open: only treat the
  // viewer as a non-author (mandate-only) when we positively know the author id
  // differs, so missing author data never wrongly locks the real owner.
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isAuthor = !module || !module.author?.id || module.author.id === currentUserId;
  const hasPublishedVersion = !!versionHistory
    && Object.values(versionHistory).some(
      (v) => (v as { workflowState?: string }).workflowState === 'published',
    );

  const formIsDirty = formState.isDirty || taskFormIsDirty;
  // Combined save pending state for both module and task saves.
  // taskSavePending comes from TaskEditor's callback (own mutation instance).
  const saveMutationPending = updateModuleMutation.isPending || taskSavePending;

  const capabilities = useModuleEditorCapabilities({
    isAuthor,
    isDraftVersion,
    isCurrentVersion,
    isPublishedVersion,
    hasPublishedVersion,
    isAnyMutationPending,
    saveMutationPending,
    isRubricGenerationJobActive,
    formIsDirty,
    taskAiEnabled,
  });

  // Content read-only = no edit capability, or an unsaved temp task placeholder.
  const effectiveIsReadOnly = !capabilities.canEditContent || isTempTask;

  // Update header title
  useEffect(() => {
    if (module) {
      screenNavigation.setOptions({
        headerTitle: `Module Editor: ${module.title} (${t('common.labels.version', { version })})`,
      });
    } else {
      screenNavigation.setOptions({
        headerTitle: 'Module Editor',
      });
    }
  }, [module, version, screenNavigation, t]);

  // Reset task form state when taskId changes
  useEffect(() => {
    setTaskFormIsDirty(false);
    setTaskSavePending(false);
  }, [taskId]);

  // The open module was deleted. The data hook refreshes the module list;
  // leave the editor so the restored address stops requesting it.
  const leftMissingModuleId = useRef<string | null>(null);
  useEffect(() => {
    if (!moduleNotFound || !moduleId) return;
    if (leftMissingModuleId.current === moduleId) return;
    leftMissingModuleId.current = moduleId;
    showToastMessage(
      'error',
      t('lecturer.moduleEditor.moduleEditor.messages.moduleNoLongerAvailable', 'This module is no longer available.'),
    );
    screenNavigation.setParams({ moduleId: undefined, version: undefined, taskId: null });
  }, [moduleNotFound, moduleId, screenNavigation, showToastMessage, t]);

  // If the selected task no longer exists in the active module/version, fall back to settings.
  useEffect(() => {
    if (!taskId || !tasks) return;
    if (taskId.startsWith('temp-')) return;
    if (createTaskMutation.isPending) return;
    const taskStillExists = tasks.some((task) => task.id === taskId);
    if (!taskStillExists) {
      screenNavigation.setParams({ taskId: null });
    }
  }, [taskId, tasks, createTaskMutation.isPending, screenNavigation]);

  // Close preview on unmount
  useEffect(() => () => closePreview(), [closePreview]);

  // Load module data into form
  const lastLoadedFormModuleRef = useRef<{ id: string, version: string } | null>(null);
  useEffect(() => {
    if (module) {
      const isNewModuleContext = lastLoadedFormModuleRef.current?.id !== module.id
        || lastLoadedFormModuleRef.current?.version !== module.version;

      const moduleDesc = module.metadata?.description || '<p><br></p>';
      const pWrappedDesc = moduleDesc.startsWith('<p>') ? moduleDesc.trim() : `<p>${moduleDesc.trim()}</p>`;

      const objectives: Record<number, string[]> = { 1: [], 2: [], 3: [] };
      const levelTitles: Record<number, string> = { 1: '', 2: '', 3: '' };

      if (module.metadata?.levels) {
        module.metadata.levels.forEach((level: ModuleLevel) => {
          objectives[level.id] = level.objectives || [];
          levelTitles[level.id] = level.title || '';
        });
      }

      reset(
        {
          description: pWrappedDesc,
          objectives,
          levelTitles,
          taskCompletionPolicy: module.metadata?.taskCompletionPolicy || null,
          taskCompletionMinLevels: (module.metadata?.taskCompletionMinLevels as unknown as Record<string, number>) || {},
          autogradeOnSubmission: module.metadata?.autogradeOnSubmission ?? false,
          examinationBasis: module.metadata?.examinationBasis ?? null,
        },
        isNewModuleContext
          ? { keepTouched: false, keepDirty: false }
          : { keepTouched: true, keepDirty: true, keepDirtyValues: true },
      );

      if (isNewModuleContext) {
        lastLoadedFormModuleRef.current = { id: module.id, version: module.version };
      }
    }
  }, [module, reset]);

  // Note: a per-task `handleSelect` used to live here but was never wired into
  // the sidebar. Task switching is now guarded at the screen level via
  // `onBeforeTaskSelect` passed to `Sidebar` from `ModuleEditor`.

  const handleVersionChange = useCallback((newVersion: string) => {
    confirmDiscardChanges({
      isDirty: formState.isDirty || taskFormIsDirty,
      showDialog,
      t,
      onDiscard: () => screenNavigation.setParams({ version: newVersion, taskId: null }),
    });
  }, [formState.isDirty, taskFormIsDirty, showDialog, t, screenNavigation]);

  const onSubmit = useCallback(async (data: ModuleEditorFormData) => {
    if (!module) return;

    const contentFieldsDirty = !!(
      dirtyFields.description
      || dirtyFields.objectives
      || dirtyFields.levelTitles
      || dirtyFields.taskCompletionPolicy
      || dirtyFields.taskCompletionMinLevels
      || dirtyFields.examinationBasis
    );
    const autogradeDirty = !!dirtyFields.autogradeOnSubmission;

    if (!contentFieldsDirty && !autogradeDirty) return;

    const saves: Promise<unknown>[] = [];

    if (contentFieldsDirty) {
      const updatedModule: ModuleUpdateRequest = {
        metadata: {
          description: data.description,
          levels: [1, 2, 3].map((id) => ({
            id,
            title: data.levelTitles[id],
            objectives: data.objectives[id],
          })),
          taskCompletionPolicy: data.taskCompletionPolicy || null,
          taskCompletionMinLevels:
            data.taskCompletionMinLevels && Object.keys(data.taskCompletionMinLevels).length > 0
              ? (data.taskCompletionMinLevels as unknown as Record<string, string>)
              : null,
          examinationBasis: data.examinationBasis ?? null,
        },
      };
      saves.push(
        updateModuleMutation.mutateAsync(
          { moduleId: module.id, moduleVersion: module.version, data: updatedModule },
        ),
      );
    }

    if (autogradeDirty) {
      saves.push(
        setAutogradeOnSubmissionMutation.mutateAsync({
          moduleId: module.id,
          data: { enabled: data.autogradeOnSubmission },
        }),
      );
    }

    try {
      await Promise.all(saves);
      showToastMessage('success', t('lecturer.moduleEditor.moduleEditor.messages.saveSuccess'));
      reset(getValues(), { keepTouched: false, keepDirty: false });
    } catch (error) {
      reportApiError(
        error as Error,
        t('lecturer.moduleEditor.moduleEditor.buttons.save'),
        'dialog',
        { expectedErrorCodes: ['MODULE_VERSIONING_ERROR'] },
      );
    }
  }, [module, dirtyFields, updateModuleMutation, setAutogradeOnSubmissionMutation, showToastMessage, t, reset, getValues, reportApiError]);

  const onFormError = useCallback((errors: FieldErrors<ModuleEditorFormData>) => {
    showToastMessage('error', t('lecturer.moduleEditor.moduleEditor.messages.formError'));
    log.info(errors);
  }, [showToastMessage, t]);

  const handleSave = useCallback(() => {
    if (taskId && taskEditorRef.current) {
      taskEditorRef.current.save();
    } else if (effectiveIsReadOnly) {
      // Published module: content fields are disabled so they can't be dirty.
      // Skip form validation (read-only fields may hold legacy data that fails
      // current rules) and submit directly — onSubmit guards via dirtyFields.
      onSubmit(getValues());
    } else {
      handleSubmit(onSubmit, onFormError)();
    }
  }, [taskId, effectiveIsReadOnly, handleSubmit, onSubmit, onFormError, getValues]);

  const handleOpenPreview = () => {
    openPreview({ module, tasks: tasks || [] });
  };

  const handleCreateVersion = () => {
    if (!module) return;
    showDialog({
      title: t('lecturer.moduleEditor.moduleEditor.dialogs.createVersion.title'),
      message: t('lecturer.moduleEditor.moduleEditor.dialogs.createVersion.message', { version: module.version }),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('lecturer.moduleEditor.moduleEditor.buttons.createVersion'),
          action: 'positive',
          preventAutoClose: true,
          onPress: async () => {
            showDialog({
              title: t('lecturer.moduleEditor.moduleEditor.dialogs.createVersion.title'),
              message: t('lecturer.moduleEditor.moduleEditor.dialogs.createVersion.loading'),
              isPersistent: true,
              buttons: [
                {
                  text: t('lecturer.moduleEditor.moduleEditor.buttons.createVersion'),
                  action: 'positive',
                  isLoading: true,
                  isDisabled: true,
                },
              ],
            });
            try {
              const response = await createVersionMutation.mutateAsync(
                { moduleId: module.id, data: { sourceVersion: module.version } },
              );
              hideDialog();
              const newVersion = response?.data?.data?.version;
              if (newVersion) screenNavigation.setParams({ version: newVersion, taskId: null });
              showToastMessage('success', t('lecturer.moduleEditor.moduleEditor.messages.createVersionSuccess'));
            } catch (error) {
              hideDialog();
              reportApiError(
                error,
                t('lecturer.moduleEditor.moduleEditor.buttons.createModuleVersion'),
                'dialog',
                {
                  expectedErrorCodes: ['MODULE_VERSIONING_ERROR', 'MODULE_VERSION_ALREADY_EXISTS'],
                },
              );
            }
          },
        },
      ],
    });
  };

  const handleDeleteVersion = () => {
    if (!module || !versionHistory) return;
    showDialog({
      title: t('lecturer.moduleEditor.moduleEditor.dialogs.deleteVersion.title'),
      message: t('lecturer.moduleEditor.moduleEditor.dialogs.deleteVersion.message', { version: module.version }),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('lecturer.moduleEditor.moduleEditor.buttons.deleteVersion'),
          action: 'negative',
          onPress: async () => {
            await deleteVersionMutation.mutateAsync(
              { moduleId: module.id, version: module.version },
              {
                onSuccess: () => {
                  const publishedVersions = Object.values(versionHistory).filter(
                    (v) => v.workflowState === 'published',
                  );
                  const sortedVersions = publishedVersions.sort((a, b) => {
                    const aParts = a.version.split('.').map(Number);
                    const bParts = b.version.split('.').map(Number);
                    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                      const aVal = aParts[i] || 0;
                      const bVal = bParts[i] || 0;
                      if (aVal !== bVal) return aVal - bVal;
                    }
                    return 0;
                  });
                  const recentPublishedVersion = sortedVersions[sortedVersions.length - 1];
                  if (recentPublishedVersion) {
                    screenNavigation.setParams({ version: recentPublishedVersion.version, taskId: null });
                  }
                  showToastMessage('success', t('lecturer.moduleEditor.moduleEditor.messages.deleteVersionSuccess'));
                },
                onError: (error) => reportApiError(error, t('lecturer.moduleEditor.moduleEditor.buttons.deleteVersion')),
              },
            );
          },
        },
      ],
    });
  };

  const requestApproval = async (
    approvalType: string,
    versionsToReplace: string[],
    changelogComment: string,
    offerFields?: { headline: string; impactNotice: string },
  ) => {
    if (!module) return;
    await requestApprovalMutation.mutateAsync(
      {
        moduleId: module.id,
        version: module.version,
        data: {
          approvalType: approvalType as ApprovalRequestApprovalType,
          versionsToReplace,
          changelog: changelogComment,
          headline: offerFields?.headline,
          impactNotice: offerFields?.impactNotice,
          invalidatesProgress: offerFields ? false : undefined,
        },
      },
      {
        onSuccess: () => {
          screenNavigation.navigate('Welcome' as never);
          showDialog({
            title: t('lecturer.moduleEditor.moduleEditor.dialogs.approvalRequested.title'),
            message: t('lecturer.moduleEditor.moduleEditor.dialogs.approvalRequested.message', { title: module.title }),
            buttons: [{ text: t('common.buttons.ok'), action: 'positive' }],
          });
        },
        onError: (error) => reportApiError(
          error,
          t('lecturer.moduleEditor.moduleEditor.buttons.requestApproval'),
          'dialog',
          {
            expectedErrorCodes: ['MODULE_VERSIONING_ERROR', 'MODULE_VERSION_REPLACEMENT_INVALID', 'VALIDATION_ERROR'],
          },
        ),
      },
    );
  };

  const handleRequestApproval = () => {
    if (!module) return;
    const isInitialDraftVersion = module.version === '0.0' && module.state?.workflowState === 'draft';

    if (isInitialDraftVersion) {
      showDialog({
        title: t('lecturer.moduleEditor.moduleEditor.dialogs.initialPublish.title'),
        message: t('lecturer.moduleEditor.moduleEditor.dialogs.initialPublish.message'),
        buttons: [
          { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
          {
            text: t('lecturer.moduleEditor.moduleEditor.dialogs.initialPublish.optionRequestApproval'),
            action: 'positive',
            onPress: () => requestApproval('publish_initial', [], ''),
          },
        ],
      });
    } else {
      const draftVersion = module.version!;
      const sourceVersion = (versionHistory as Record<string, VersionHistoryEntry>)?.[draftVersion]?.sourceVersion || '0.0';
      const mayRequestAsNewVersion = Object.values(versionHistory).some((v: VersionHistoryEntry) => v.workflowState === 'published');

      changelogMethods.reset({
        approvalType: (mayRequestAsNewVersion ? 'new_version' : 'replace_version') as ModuleApprovalType,
        changelogComment: '',
        offerStudentNote: '',
      });

      requestApprovalDialogActiveRef.current = true;

      showDialog({
        size: 'lg',
        title: t('lecturer.moduleEditor.moduleEditor.dialogs.requestApprovalOptions.title'),
        message: '',
        children: (
          <FormProvider {...changelogMethods}>
            <ModuleApprovalRequestFields
              moduleId={module.id!}
              sourceVersion={sourceVersion}
              draftVersion={draftVersion}
              mayRequestAsNewVersion={mayRequestAsNewVersion}
            />
          </FormProvider>
        ),
        buttons: [
          { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
          {
            text: t('lecturer.moduleEditor.moduleEditor.dialogs.requestApprovalOptions.submitButton', {
              defaultValue: 'Request approval',
            }),
            action: 'positive',
            preventAutoClose: true,
            onPress: (closeDialog) => {
              changelogMethods.handleSubmit((data) => {
                const choice = data.approvalType as ModuleApprovalType;
                if (choice === 'replace_version') {
                  requestApproval('replace_version', [sourceVersion], data.changelogComment);
                } else if (choice === 'new_version') {
                  requestApproval('new_version', [], data.changelogComment);
                } else if (choice === 'offer_upgrade') {
                  requestApproval('offer_upgrade', [], data.changelogComment, {
                    headline: '',
                    impactNotice: data.offerStudentNote?.trim() || '',
                  });
                }
                closeDialog?.();
              })();
            },
          },
        ],
      });
    }
  };

  const handleDeleteModule = () => {
    if (!module) return;
    showDialog({
      title: t('lecturer.moduleEditor.moduleEditor.dialogs.deleteModule.title', {
        defaultValue: 'Delete Entire Module',
      }),
      message: t('lecturer.moduleEditor.moduleEditor.dialogs.deleteModule.message', {
        defaultValue: 'Are you sure you want to delete this entire module and all its versions? This action cannot be undone.',
        title: module.title,
      }),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('common.buttons.delete'),
          action: 'negative',
          onPress: async () => {
            await deleteModuleMutation.mutateAsync(
              { moduleId: module.id },
              {
                onSuccess: () => {
                  showToastMessage('success', t('lecturer.moduleEditor.moduleEditor.messages.deleteModuleSuccess', {
                    defaultValue: 'Module deleted successfully',
                  }));
                  screenNavigation.navigate({ name: 'EvaluationDesk', params: {}, merge: false } as never);
                },
                onError: (error) => reportApiError(error, t('lecturer.moduleEditor.moduleEditor.buttons.deleteModule', {
                  defaultValue: 'Delete Module',
                })),
              },
            );
          },
        },
      ],
    });
  };

  const handleGenerateAllRubrics = useCallback(() => {
    if (!module || !taskAiEnabled) return;

    const eligibleTaskIds = getEligibleRubricGenerationTaskIds(tasks);
    if (eligibleTaskIds.length === 0) {
      showToastMessage(
        'info',
        t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsNothingToDo'),
        t('lecturer.moduleEditor.moduleEditor.buttons.generateAllRubrics'),
      );
      return;
    }

    showDialog({
      title: t('lecturer.moduleEditor.moduleEditor.dialogs.generateAllRubrics.title'),
      message: t('lecturer.moduleEditor.moduleEditor.dialogs.generateAllRubrics.message', {
        count: eligibleTaskIds.length,
        moduleTitle: module.title,
      }),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('lecturer.moduleEditor.moduleEditor.buttons.generateAllRubrics'),
          onPress: () => {
            launchRubricGenerationJob(
              eligibleTaskIds,
              t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsJobLabel', {
                moduleTitle: module.title,
              }),
            );
          },
        },
      ],
    });
  }, [
    module,
    taskAiEnabled,
    tasks,
    showDialog,
    showToastMessage,
    t,
    launchRubricGenerationJob,
  ]);

  return {
    t,
    module,
    moduleId,
    version,
    tasks,
    tasksQueryKey,
    isLoading,
    isModuleLoading,
    versionHistory,
    methods,
    taskId,
    hasModule: !!moduleId,
    effectiveIsReadOnly,
    isTempTask,
    isAnyMutationPending,
    isDraftVersion,
    isCurrentVersion,
    isAuthor: capabilities.isAuthor,
    editLockReason: capabilities.editLockReason,
    canCreateVersion: capabilities.canCreateVersion,
    canDeleteVersion: capabilities.canDeleteVersion,
    canDeleteModule: capabilities.canDeleteModule,
    canRequestApproval: capabilities.canRequestApproval,
    canGenerateRubrics: capabilities.canGenerateRubrics,
    saveMutationPending,
    createVersionMutationPending: createVersionMutation.isPending,
    deleteVersionMutationPending: deleteVersionMutation.isPending,
    deleteModuleMutationPending: deleteModuleMutation.isPending,
    requestApprovalMutationPending: requestApprovalMutation.isPending,
    taskAiEnabled,
    isRubricGenerationJobActive,
    isLaunchingRubricJob,
    handleSave,
    handleOpenPreview,
    handleCreateVersion,
    handleDeleteVersion,
    handleDeleteModule,
    handleRequestApproval,
    handleGenerateAllRubrics,
    handleVersionChange,
    taskEditorRef,
    setTaskFormIsDirty,
    setTaskSavePending,
    formIsDirty,
    autogradeIsReadOnly: !capabilities.canEditAutograde || isTempTask,
  };
};
